/**
 * [NF-385] Shared state between the checkout comment field and the rest of
 * checkout.
 *
 * `satisfied` gates the payment step when the comment is required: it starts
 * false for a required field and flips true only once a non-empty comment is
 * saved, so `Checkout.vue` can keep the payment adapter unmounted until then
 * (a bare `required` attribute on a textarea outside a <form> enforces nothing).
 * When the field is disabled or optional it is always true.
 *
 * `pendingSave` holds the in-flight save promise so a place-order flow that does
 * not wait for a provider iframe (ManualInvoice) can await it before completing,
 * instead of racing the blur-triggered save.
 */
export const useCheckoutComment = () => {
	const { enabled, required, maxLength } = useRuntimeConfig().public.checkout
		.comment as { enabled: boolean; required: boolean; maxLength: number };

	// "Required" only means anything when the feature is on. Folding `enabled` in
	// here keeps a REQUIRED=true + ENABLED=false client from gating the payment
	// step with no comment box to unlock it (there would be no way to pay).
	const effectiveRequired = enabled && required;

	const satisfied = useState<boolean>(
		"checkoutCommentSatisfied",
		() => !effectiveRequired
	);
	const pendingSave = useState<Promise<unknown> | null>(
		"checkoutCommentPendingSave",
		() => null
	);

	return {
		enabled,
		required: effectiveRequired,
		maxLength,
		satisfied,
		pendingSave
	};
};
